import { captureError } from '@leagueplatform/observability';
import {
  AnalyticsAuthAbortError,
  DEFAULT_MAX_RETRIES,
  DEFAULT_RETRY_BASE_DELAY_MS,
  DEFAULT_RETRY_MAX_DELAY_MS,
  type AnalyticsSdkEvent,
  type RetryPolicy,
  type Transport,
} from './analytics-sdk.types';

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getBackoffDelay = (
  attempt: number,
  baseDelay: number,
  maxDelay: number,
): number => {
  const exponential = baseDelay * 2 ** attempt;
  const jitter = Math.random() * baseDelay;
  return Math.min(exponential + jitter, maxDelay);
};

export const createRetryPolicy = (
  transport: Transport,
  maxRetries = DEFAULT_MAX_RETRIES,
  baseDelayMs = DEFAULT_RETRY_BASE_DELAY_MS,
  maxDelayMs = DEFAULT_RETRY_MAX_DELAY_MS,
): RetryPolicy => ({
  sendWithRetry: async (
    batch: AnalyticsSdkEvent[],
  ): Promise<AnalyticsSdkEvent[]> => {
    let remaining = batch;
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      if (attempt > 0) {
        // eslint-disable-next-line no-await-in-loop
        await delay(getBackoffDelay(attempt - 1, baseDelayMs, maxDelayMs));
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        remaining = await transport.send(remaining);
        lastError =
          remaining.length > 0
            ? new Error(
                `Transport returned ${remaining.length} unsent event(s)`,
              )
            : undefined;
      } catch (error) {
        if (error instanceof AnalyticsAuthAbortError) return error.unsent;
        lastError = error;
        // Transport threw — entire remaining batch needs retry.
      }

      if (remaining.length === 0) return [];
    }

    if (remaining.length > 0) {
      captureError(
        new Error(
          `Analytics: batch failed all retries; ${remaining.length} event(s) remain queued and will be retried on the next flush interval or session.`,
          { cause: lastError },
        ),
      );
    }

    return remaining;
  },
});
